// src/pages/CaseStudiesPage.js
// Anonymized case studies: problem / data / architecture / integration / outcome / tech.
import React from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../components/SEO/PageMeta';
import StructuredData from '../components/SEO/StructuredData';
import CtaBanner from '../components/CtaBanner';

const Field = ({ label, text }) => (
    <div>
        <p className="text-xs font-bold uppercase tracking-wide text-accent mb-1">{label}</p>
        <p className="text-gray-700 text-sm text-justify">{text}</p>
    </div>
);

const CaseStudiesPage = () => {
    const { t } = useTranslation();
    const items = t('case_studies_page.items', { returnObjects: true }) || [];
    const labels = t('case_studies_page.labels', { returnObjects: true }) || {};

    return (
        <>
            <PageMeta
                title={t('case_studies_page.meta_title')}
                description={t('case_studies_page.meta_description')}
                path="/case-studies"
            />
            <StructuredData type="page" page={{ name: t('nav.case_studies'), path: '/case-studies', kind: 'CollectionPage', description: t('case_studies_page.meta_description') }} />

            {/* Page header */}
            <div className="bg-slate-900 py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
                    <h1 className="text-4xl font-bold text-white mb-4">{t('case_studies_page.title')}</h1>
                    <p className="text-lg text-gray-300 max-w-3xl mx-auto">{t('case_studies_page.subtitle')}</p>
                </div>
            </div>

            <div className="bg-gray-100 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-8">
                    <div className="grid lg:grid-cols-2 gap-8">
                        {Array.isArray(items) && items.map((cs, i) => (
                            <article key={i} className="bg-white rounded-xl shadow-xl p-6 flex flex-col" data-aos="fade-up" data-aos-delay={(i % 2) * 100}>
                                <h2 className="text-xl font-bold text-primary mb-4">{cs.title}</h2>
                                <div className="space-y-3 flex-grow">
                                    <Field label={labels.problem} text={cs.problem} />
                                    <Field label={labels.data} text={cs.data} />
                                    <Field label={labels.architecture} text={cs.architecture} />
                                    <Field label={labels.integration} text={cs.integration} />
                                    <Field label={labels.outcome} text={cs.outcome} />
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                    <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">{labels.tech}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {(cs.tech || '').split(',').map((tech, ti) => (
                                            <span key={ti} className="bg-gray-100 text-gray-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                                                {tech.trim()}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </div>

            <CtaBanner
                title={t('solutions_common.cta_title')}
                text={t('solutions_common.cta_text')}
                buttonLabel={t('solutions_common.cta_button')}
            />
        </>
    );
};

export default CaseStudiesPage;
